import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Size } from '@syconium/little-miss-figgy'

import { useCartDialog } from '../../../../../../../../../app/_providers/CartProvider.client'
import { areFitsEquivalent } from '../../../../../../../../../lib/utils'
import { Fit } from '../../../../../../../../constants'
import { QuickBuyContainer } from '../../../../../../../../containers/QuickBuyContainer'
import { EmbroideryConfig } from '../../../../../../../../containers/QuickBuyContainer/types'

import type {
  EmbroideryVariant,
  FavoritesPreferences,
  Variant,
} from '../../../../../../../../../types/graphql'

export interface IArgs {
  activeProductFinalSale: boolean | undefined
  category: string
  coloredVariants: Variant[]
  ctaOnly?: boolean
  embroideryConfig?: EmbroideryConfig
  fitKeys: string[]
  onAddToCart?: () => void
  openQuickBuyModal(): void
  preferences?: FavoritesPreferences
  selectedFitKey: string | undefined
  setSelectedFitKey: (fit: string | undefined) => void
  sizes: Size[]
  supportsHover: boolean | null
  rawCategory?: string
}

export interface IProgress {
  embroidery?: { variant?: EmbroideryVariant }
  fitKey?: string
  started?: true
  variant?: Variant
  swPromoEligible?: boolean
}

type StepInitial = {
  completed: boolean
  enabled: boolean
  onSelect: () => void
  ref: RefObject<HTMLButtonElement | null>
}

type StepFit = {
  completed: boolean
  enabled: boolean
  onSelect: (fit: string) => void
  ref: RefObject<HTMLDivElement | null>
}

type StepSize = {
  completed: boolean
  enabled: boolean
  onSelect: (size: Size, swPromoEnabled?: boolean) => void
  ref: RefObject<HTMLDivElement | null>
}

type StepEmbroidery = {
  completed: boolean
  enabled: boolean
  onSelect: (selected: boolean) => void
  ref: RefObject<HTMLDivElement | null>
}

type StepAdding = {
  completed: boolean
  enabled: boolean
  ref: RefObject<HTMLDivElement | null>
}

type Steps = {
  initial: StepInitial
  fit: StepFit
  size: StepSize
  embroidery: StepEmbroidery
  adding: StepAdding
}

export const allPossibleSteps = ['initial', 'fit', 'size', 'embroidery', 'adding'] as const

export function useQuickBuyOverlayController({
  activeProductFinalSale,
  coloredVariants,
  ctaOnly = false,
  embroideryConfig,
  fitKeys,
  openQuickBuyModal,
  onAddToCart,
  preferences,
  selectedFitKey,
  setSelectedFitKey,
  sizes,
  supportsHover,
}: IArgs) {
  const { open: openCartDialog } = useCartDialog()
  const quickBuyService = QuickBuyContainer.useContainer()

  const [progress, setProgress] = useState<IProgress>({})
  const [selectedSize, setSelectedSize] = useState<Size | undefined>()
  const [height, setHeight] = useState<number>(0)
  const [offset, setOffset] = useState<number>(0)
  const [previousFitsKeys, setPreviousFitsKeys] = useState(fitKeys)

  if (fitKeys.toString() !== previousFitsKeys.toString()) {
    setPreviousFitsKeys(fitKeys)
    setSelectedFitKey(undefined)
  }

  const initialRef = useRef<HTMLButtonElement>(null)
  const fitRef = useRef<HTMLDivElement>(null)
  const sizeRef = useRef<HTMLDivElement>(null)
  const embroideryRef = useRef<HTMLDivElement>(null)
  const addRef = useRef<HTMLDivElement>(null)

  const steps = useMemo(() => {
    const fitStepEnabled = fitKeys.length > 0
    const fitStepCompleted = !!selectedFitKey

    const sizeStepEnabled = sizes.length > 0
    const sizeStepCompleted = !!progress.variant

    const emboideryStepEnabled = !!embroideryConfig
    const emboideryStepCompleted = !!progress.embroidery

    const steps: Steps = {
      initial: {
        completed: !!progress.started,
        enabled: true,
        onSelect: openQuickBuyModal,
        ref: initialRef,
      },
      fit: {
        completed: fitStepCompleted,
        enabled: fitStepEnabled,
        onSelect: (fitKey: string) => {
          const firstButton = sizeRef.current?.querySelector(
            'button:not([disabled])'
          ) as HTMLElement
          firstButton?.focus({ preventScroll: true })
          setSelectedFitKey(fitKey)
        },
        ref: fitRef,
      },
      size: {
        completed: sizeStepCompleted,
        enabled: sizeStepEnabled,
        onSelect: setSize,
        ref: sizeRef,
      },
      embroidery: {
        completed: emboideryStepCompleted,
        enabled: emboideryStepEnabled,
        onSelect: (selected: boolean) =>
          setProgress(state => ({
            ...state,
            embroidery: { variant: selected ? embroideryConfig?.variant : undefined },
          })),
        ref: embroideryRef,
      },
      adding: {
        completed: false,
        enabled:
          (sizeStepEnabled || emboideryStepEnabled) &&
          (sizeStepCompleted || emboideryStepCompleted),
        ref: addRef,
      },
    }
    return steps
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    coloredVariants,
    ctaOnly,
    openQuickBuyModal,
    progress,
    fitKeys,
    sizes,
    embroideryConfig,
    supportsHover,
  ])

  const resetProgress = useCallback(() => {
    const newState: IProgress = {}
    if (fitKeys.length === 1 && fitKeys[0] === Fit.REGULAR) {
      setSelectedFitKey(fitKeys[0])
    } else {
      setSelectedFitKey(undefined)
    }

    newState.started = progress.started
    setProgress(newState)
  }, [fitKeys, progress.started, setSelectedFitKey])

  const addToCart = useCallback(() => {
    if (!progress.variant) return

    quickBuyService.addToCart({
      embroideryConfig,
      embroideryVariant: progress.embroidery?.variant,
      finalSale: activeProductFinalSale,
      swPromoEligible: progress.swPromoEligible,
      variant: progress.variant,
    })

    openCartDialog()

    setTimeout(() => {
      resetProgress()
    }, 300)
  }, [
    progress.variant,
    progress.embroidery?.variant,
    progress.swPromoEligible,
    quickBuyService,
    embroideryConfig,
    activeProductFinalSale,
    openCartDialog,
    resetProgress,
  ])

  // the default fit is different depending on language, so just match all fits
  // this is used to derive sizes, and only unique sizes will be used
  const getDefaultFit = (fit?: string) => fit ?? '*'

  function setSize(size: Size, swPromoEligible?: boolean) {
    const variant = coloredVariants.find(v => v.size === size.shorthand)
    if (!variant || size.soldOut) return
    setProgress(state => ({ ...state, swPromoEligible, variant }))
  }

  // The current step is the first enabled, but uncompleted step
  const step = useMemo(() => {
    return allPossibleSteps.find(step => steps[step].enabled && !steps[step].completed) ?? 'initial'
  }, [steps])

  const previousStepRef = useRef(step)
  if (previousStepRef.current !== step) {
    previousStepRef.current = step

    // Add to cart the item when we get to the final step
    if (step === 'adding' && progress.variant) {
      try {
        addToCart()
      } finally {
        if (onAddToCart) {
          onAddToCart()
        }
      }
    }
  }

  useEffect(() => {
    // Jump through initial step if we have fits/sizes + hover support/no CTA
    const shouldJumpInitialStep =
      (fitKeys.length || sizes.length) && supportsHover === true && !ctaOnly && step === 'initial'
    if (shouldJumpInitialStep) {
      setProgress(state => ({ ...state, started: true }))
    }
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKeys, sizes])

  useEffect(() => {
    if (preferences) {
      if (preferences.fit && !selectedFitKey) {
        setProgress(state => ({ ...state, started: true, fit: preferences.fit }))
      }
      if (preferences.size) {
        const variant = coloredVariants.find(
          v =>
            areFitsEquivalent(v.fit, getDefaultFit(selectedFitKey)) && v.size === preferences.size
        )
        if (variant) {
          setSelectedSize({
            shorthand: preferences.size,
            soldOut: false,
          })
        }
      }
    }
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences, progress])

  useEffect(() => {
    const current = steps[step].ref.current
    setHeight(current?.offsetHeight ?? 0)
    setOffset(current?.offsetTop ?? 0)
  }, [steps, step])

  return {
    embroideryConfig,
    height,
    offset,
    resetProgress,
    selectedFitKey,
    selectedSize,
    sizes,
    step,
    steps,
  }
}
